import type {PageParam, PaginatedMemexItemsQueryVariables, SliceByQueryVariables} from './types'

export const paginatedMemexItemsPrimaryQueryKey = 'paginatedMemexItems'
export const activeQueriesMetadataPrimaryQueryKey = 'activeQueriesMetadata'
export const sliceByDataPrimaryQueryKey = 'sliceByData'
export const totalCountsPrimaryKey = 'paginatedTotalCounts'

type GroupedPaginatedMemexItemsQueryKey = [
  typeof paginatedMemexItemsPrimaryQueryKey,
  PaginatedMemexItemsQueryVariables,
  NonNullable<GroupId>,
  PageParam,
]

type UngroupedPaginatedMemexItemsQueryKey = [
  typeof paginatedMemexItemsPrimaryQueryKey,
  PaginatedMemexItemsQueryVariables,
  undefined,
  PageParam,
]

export type PaginatedMemexItemsQueryKey = GroupedPaginatedMemexItemsQueryKey | UngroupedPaginatedMemexItemsQueryKey

export type ActiveQueriesMetadataQueryKey = [
  typeof paginatedMemexItemsPrimaryQueryKey,
  PaginatedMemexItemsQueryVariables,
  typeof activeQueriesMetadataPrimaryQueryKey,
]

export type SliceByDataQueryKey = [typeof sliceByDataPrimaryQueryKey, SliceByQueryVariables]

export type TotalCountsDataQueryKey = [typeof totalCountsPrimaryKey, PaginatedMemexItemsQueryVariables]

export const groupIdForUngroupedData = undefined
export type GroupId = string | typeof groupIdForUngroupedData

export const buildMemexItemsPageQueryKey = (
  variables: PaginatedMemexItemsQueryVariables,
  groupId: GroupId,
  pageParam: PageParam,
): PaginatedMemexItemsQueryKey => {
  return [paginatedMemexItemsPrimaryQueryKey, variables, groupId, pageParam]
}

export const buildActiveQueriesMetadataQueryKey = (
  variables: PaginatedMemexItemsQueryVariables,
): ActiveQueriesMetadataQueryKey => {
  return [paginatedMemexItemsPrimaryQueryKey, variables, activeQueriesMetadataPrimaryQueryKey]
}

export const buildSliceDataQueryKey = (variables: SliceByQueryVariables): SliceByDataQueryKey => {
  return [sliceByDataPrimaryQueryKey, variables]
}

export const buildPaginatedTotalCountsQueryKey = (
  variables: PaginatedMemexItemsQueryVariables,
): TotalCountsDataQueryKey => {
  return [totalCountsPrimaryKey, variables]
}

/**
 * A utility method for looking up the groupId from a `react-query` `QueryKey`. The `QueryKey`
 * is implemented as an Array under the hood. It's possible for the groupId to be `undefined`
 * if the data represented by the key is ungrouped.
 * @param queryKey A query key for paginated memex items data.
 * @returns The groupId from the query key as a string, or `undefined` if the data query key is ungrouped.
 */
export const getGroupIdFromQueryKey = (queryKey: PaginatedMemexItemsQueryKey): GroupId => {
  return queryKey[2]
}

export const isQueryKeyForGroups = (queryKey: PaginatedMemexItemsQueryKey): boolean => {
  // A query key that looks like:
  // ['paginatedMemexItems', {q: '', sortedBy: [], horizontalGroupedByColumnId: 'Status'}, undefined, undefined]
  // Represents a page of groups
  return (
    queryKey[2] === groupIdForUngroupedData &&
    (queryKey[1].horizontalGroupedByColumnId != null || queryKey[1].verticalGroupedByColumnId != null)
  )
}
